import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin, StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import ScrollingLogosWidget from '../../widgets/animated/ScrollingLogosWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';

type AIAssistantEngineeringStaticProps = {
  plugins: StrapiApiPlugin[];
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<AIAssistantEngineeringStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: AIAssistantEngineeringStaticProps = { plugins, scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const AIAssistantEngineering: NextPageWithLayout<AIAssistantEngineeringStaticProps> = (props) => {
  const { plugins, scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.WorkAssistantForEngineering);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Work Assistant for Engineering Teams"
        title="Faster answers, fewer blockers"
        description="Our AI assistant brings the knowledge of your codebase, documentation, and team conversations right to your developers—wherever they work."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="AI capabilities for Engineering teams" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-express-approvals'),
              orientation: 'left',
              title: 'Full Clarity',
              description:
                'Start every project with full context—avoid duplicating features and stay on top of tech debt effortlessly. Empower your team to make informed decisions faster with unified project visibility.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-private-secure-support'),
              orientation: 'right',
              title: 'Expertise Unlocked',
              description:
                'Quickly gain deep understanding of any topic with AI trained on your team’s documentation, code updates, conversations, and more. Instantly surface relevant knowledge and best practices when your engineers need them most.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-self-service'),
              orientation: 'left',
              title: 'Create Without Friction',
              description:
                'Reduce interruptions and let your teams get unstuck fast using GenAI-powered answers without pulling in engineering. Enable seamless self-service support so developers can focus on building.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-it-service-management'),
              orientation: 'right',
              title: 'AI-Powered IT Service Management',
              description:
                'Resolve IT issues automatically, enhance your knowledge base, and streamline ticketing with AI-driven workflows. Free your engineers to focus on innovation.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-jira-data-center'),
              orientation: 'left',
              title: 'Seamless Jira Integration',
              description:
                'Supercharge your engineering support with the only AI assistant that works natively on Jira Data Center and JSM Cloud. Enjoy instant productivity with zero switching costs.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-employee-onboarding'),
              orientation: 'right',
              title: 'Frictionless Developer Onboarding',
              description:
                'Accelerate onboarding for new engineers with automated knowledge delivery, instant access to documentation, and AI-guided best practices from day one.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} hideSubtitle={true} />
      </PageSection>
    </>
  );
};
AIAssistantEngineering.displayName = 'AIAssistantEngineering';
export default AIAssistantEngineering;
